import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { PROMO_POPUP_ID } from 'reactApp/appHelpers/featuresHelpers/features/logInAccountModal';
import { getPublicItemById, getPublicList, getPublicRootWeblink } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/Mobile/LogInAccountModal/LogInAccountModal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const logInAccountModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        PROMO_POPUP_ID,
        models.base,
        new BaseModelProps(PROMO_POPUP_ID, {
            store: new LocalStorageStore(PROMO_POPUP_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_POPUP_ID, SuspendedComponent, render.react),
            closable: true,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            test: (resolve, reject) => {
                if (!IS_PHONE_BROWSER) {
                    reject();
                    return;
                }
                const state = reduxStore.getState();
                const { weblink } = getPublicRootWeblink(state);
                const item = getPublicItemById(state, weblink);
                const storage = getCurrentStorage(state);
                const publicList = getPublicList(reduxStore.getState());
                const currentPublic = Object.values(publicList)[0];

                if (
                    storage !== EStorageType.public ||
                    (currentPublic?.kind !== 'video' && currentPublic?.kind !== 'image' && !item?.isFolder)
                ) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_POPUP_ID, 'rejectbyaction']);
                    return;
                }

                const isAnonymous = UserSelectors.isAnonymous(state);

                const isReject = IS_WEBVIEW || !isAnonymous || !IS_PHONE_BROWSER;

                sendXray(['promo-test', PROMO_POPUP_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
